<template>
  <div class="calendar-workspace">
    <div class="calendar-heading">
      <div class="calendar-heading__title"><span class="calendar-heading__icon"><app-icon name="calendar" :size="23" /></span><div><div class="calendar-eyebrow">Online konzultácie</div><h2>{{ consultationClient ? `Termín pre ${consultationClient.first_name} ${consultationClient.last_name}` : 'Kalendár' }}</h2></div></div>
      <button type="button" class="calendar-icon-button calendar-close" aria-label="Zavrieť kalendár" title="Zavrieť kalendár" @click="$emit('close')"><app-icon name="close" :size="20" /></button>
    </div>
    <div class="calendar-toolbar">
      <div class="calendar-date-controls">
        <button type="button" class="calendar-today-button" @click="goToday">Dnes</button>
        <div class="calendar-arrows"><button type="button" class="calendar-icon-button" :aria-label="mode === 'Day' ? 'Predchádzajúci deň' : 'Predchádzajúci týždeň'" @click="previousWeek"><app-icon name="arrow-right" class="calendar-arrow-back" :size="18" /></button><button type="button" class="calendar-icon-button" :aria-label="mode === 'Day' ? 'Nasledujúci deň' : 'Nasledujúci týždeň'" @click="nextWeek"><app-icon name="arrow-right" :size="18" /></button></div>
        <strong class="calendar-range" aria-live="polite">{{ dateRange }}</strong>
      </div>
      <div class="calendar-toolbar__actions">
        <div v-if="!compact" class="calendar-view-switch" aria-label="Zobrazenie kalendára"><button type="button" :aria-pressed="mode === 'WorkWeek'" @click="setView('WorkWeek')">Týždeň</button><button type="button" :aria-pressed="mode === 'Day'" @click="setView('Day')">Deň</button></div>
        <button type="button" class="calendar-create-button" @click="openConsultation()"><app-icon name="plus" :size="18" /><span>{{ consultationClient ? 'Zadať termín' : 'Nová konzultácia' }}</span></button>
      </div>
    </div>
    <div class="calendar-grid-card">
      <div class="calendar-day-headers" :style="{gridTemplateColumns: `52px repeat(${visibleDays.length}, minmax(0, 1fr))`}">
        <div class="calendar-timezone" title="Miestny čas">Čas</div>
        <div v-for="day in visibleDays" :key="day.key" class="calendar-day" :class="{'is-today': day.today}"><span>{{ day.weekday }}</span><strong>{{ day.number }}</strong><small v-if="day.today">dnes</small></div>
      </div>
      <div ref="canvas" class="calendar-canvas"><div id="calendar" aria-label="Termíny v kalendári"></div></div>
    </div>
    <div class="calendar-footer"><span class="calendar-selection-hint"><app-icon name="plus" :size="14" /> Vyber voľný čas v kalendári a vytvor konzultáciu.</span><div class="calendar-legend"><span><i class="calendar-legend__meeting" />Dohodnuté</span><span><i class="calendar-legend__available" />Voľné termíny</span></div></div>
  </div>
</template>

<script>
import { DayPilot } from '@daypilot/daypilot-lite-javascript/daypilot-javascript.min'
import translateFromDBValue from "../../mixins/translateFromDBValue";
import {mapGetters} from "vuex";
import AppIcon from "./AppIcon.vue";

export default {
  name: "CalendarCustom",
  components: { AppIcon },
  mixins: [
    translateFromDBValue
  ],
  watch: {
    eventUsersGetter(users) {
      this.eventUsers = users;
      this.refreshCalendarEvents();
    },
    calendarEventsGetter(events) {
      this.refreshCalendarEvents();
    },
  },
  computed: {
    compact() { return this.viewportWidth <= 600; },
    visibleDays() {
      const start = new Date(`${this.focusDate}T12:00:00`);
      if (this.mode === 'WorkWeek') start.setDate(start.getDate() - (start.getDay() + 6) % 7);
      return Array.from({length: this.mode === 'Day' ? 1 : 5}, (_, index) => {
        const date = new Date(start); date.setDate(date.getDate() + index);
        const key = this.localDate(date);
        return {key, number: date.getDate(), weekday: date.toLocaleDateString('sk-SK', {weekday: 'short'}), today: key === this.localDate(new Date())};
      });
    },
    dateRange() {
      const days = this.visibleDays;
      const first = new Date(`${days[0].key}T12:00:00`), last = new Date(`${days[days.length - 1].key}T12:00:00`);
      const full = date => date.toLocaleDateString('sk-SK', {day: 'numeric', month: 'long', year: 'numeric'});
      if (this.mode === 'Day') return full(first);
      return `${first.getDate()}.${first.getMonth() === last.getMonth() ? '' : ` ${first.getMonth() + 1}.`} – ${full(last)}`;
    },
    ...mapGetters({
      eventUsersGetter: "clientsEvents/getEventsUsers",
      calendarEventsGetter: "clientsEvents/getEvents",
      consultationClient: "clientsEvents/getConsultationClient",
    })
  },
  beforeDestroy() {
    this.$root.$off("event-delete", this.deleteEvent);
    window.removeEventListener('resize', this.handleResize);
    this.resizeObserver?.disconnect();
    this.calendar?.dispose();
  },
  created () {
    this.eventUsers = this.$store.getters["clientsEvents/getEventsUsers"];
    this.events = this.$store.getters["clientsEvents/getEvents"];
  },
  mounted () {
    this.$root.$on("event-delete", this.deleteEvent);
    this.setTodayFullDate();

    this.viewportWidth = window.innerWidth;
    this.mode = this.compact ? 'Day' : 'WorkWeek';

    this.calendar = new DayPilot.Calendar("calendar", {
      viewType: this.mode,
      heightSpec: 'BusinessHours', businessBeginsHour: 8, businessEndsHour: 18,
      cellHeight: 28, headerHeight: 0, hourWidth: 52, timeFormat: 'Clock24Hours',
      durationBarVisible: false, eventResizeHandling: 'Disabled',
      startDate: this.focusDate,
      eventMoveHandling: "disabled",
      eventDeleteHandling: "CallBack",
      timeRangeSelectedHandling: "Enabled",
      onTimeRangeSelected: args => {
        this.openConsultation(args.start.value, args.end.value);
        this.calendar.clearSelection();
      },
      onEventClicked: args => {
        if (this.deleting) return;
        this.eventExtendedView(args)
      },
      onEventMoved: args => {
        this.setTodayFullDate();
        this.moveEvent({event: {...args.e.data, start: {value: args.newStart.value}, end: {value: args.newEnd.value}}, checkEventChange: true});
      },
      onEventDeleted: async args => {
        this.deleting = true;
        await this.deleteEvent({event: args, checkEventChange: true});
        this.deleting = false;
      }
    });

    this.calendar.locale = "sk-sk";
    this.calendar.init();

    this.events = this.transformEvents(this.events);
    this.calendar.update({events: this.events});
    window.addEventListener('resize', this.handleResize);
    this.resizeObserver = new ResizeObserver(this.fitGrid);
    this.resizeObserver.observe(this.$refs.canvas);
    this.$nextTick(this.fitGrid);
  },
  methods: {
    localDate(date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; },
    refreshCalendarEvents() { this.events = this.transformEvents(this.calendarEventsGetter || []); this.calendar?.update({events: this.events}); },
    handleResize() { this.viewportWidth = window.innerWidth; if (this.compact && this.mode !== 'Day') this.setView('Day'); this.$nextTick(this.fitGrid); },
    fitGrid() {
      if (!this.calendar || !this.$refs.canvas) return;
      const cellHeight = Math.max(16, Math.floor((this.$refs.canvas.clientHeight - 2) / 20));
      if (cellHeight !== this.calendar.cellHeight) this.calendar.update({cellHeight});
    },
    setView(mode) { this.mode = mode; this.calendar?.update({viewType: mode, startDate: this.focusDate}); this.$nextTick(this.fitGrid); },
    goToday() {
      this.focusDate = this.localDate(new Date());
      if ([0, 6].includes(new Date().getDay())) this.mode = 'Day';
      this.calendar?.update({startDate: this.focusDate, viewType: this.mode});
    },
    shiftDate(direction) {
      const date = new Date(`${this.focusDate}T12:00:00`);
      date.setDate(date.getDate() + direction * (this.mode === 'Day' ? 1 : 7));
      this.focusDate = this.localDate(date); this.calendar.update({startDate: this.focusDate});
    },
    escapeEventText(value) { return String(value).replace(/[&<>"']/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[character])); },
    openConsultation(start = null, end = null) {
      const hasKnownClient = Boolean(this.consultationClient);
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: {
          type: hasKnownClient ? "brokerConsultationKnown" : "brokerConsultation",
          component: "OnlineConsultationEvent"
        },
        payload: {
          start,
          end,
          ...(hasKnownClient ? {client: this.consultationClient} : {}),
        },
        component: "ClientEventPopUp",
        rules: ["priority"],
      });
    },
    setTodayFullDate() {
      const date = new Date();
      date.setHours(date.getHours() + 1);
      this.todayFull = date.toISOString().substr(0, 19);
    },
    transformDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric', hour: "numeric", minute: "numeric"
      });
    },
    getStartAndEndTime(start, end) {
      if (!start || !end) return "";
      return `${start.slice(11, 16)} - ${end.slice(11, 16)}`;
    },
    getUserLastnameById(id) {
      if (typeof id === "undefined" || id === null) return "";
      const userIndex = this.eventUsers.map(e => e.id).indexOf(id);
      return userIndex >= 0 ? this.eventUsers[userIndex].last_name : "";
    },
    getUserIdFromEventUsers(event) {
      let clientId = null;
      const eventUserIds = this.eventUsers.map(e => e.id);
      for (const eventUser of event.users) {
        if (!eventUserIds.includes(eventUser)) continue;
        clientId = eventUser;
      }
      return clientId;
    },
    transformEventText(event) {
      if (event.meta?.marketing_plan) return `Marketing: ${event.description || "Aktivita"}\n${this.getStartAndEndTime(event.start, event.end)}`;
      if (event.is_available_for_booking) {
        return `Voľný termín pre klientov\n${this.getStartAndEndTime(event.start, event.end)}`;
      }
      const clientId = this.getUserIdFromEventUsers(event);
      const clientName = this.getUserLastnameById(clientId);
      return `${this.translateEventType(event.type)}${clientName ? `\n${clientName}` : ""}\n${this.getStartAndEndTime(event.start, event.end)}`;
    },
    transformEvents(events) {
      const eventsArray = [];
      for (const event of events) {
        eventsArray.push({
          ...event,
          start: event.start.slice(0, 19),
          end: event.end ? event.end.slice(0, 19) : event.start.slice(0, 19),
          text: this.transformEventText(event),
          id: event.id,
          html: event.is_available_for_booking ? `<div class="calendar-event-compact">${this.escapeEventText(event.start.slice(11,16))} · Voľný termín</div>` : `<div class="calendar-event-time">${this.escapeEventText(this.getStartAndEndTime(event.start, event.end))}</div><div class="calendar-event-title">${this.escapeEventText(this.transformEventText(event).split('\n').slice(0, -1).join(' · '))}</div>`,
          cssClass: event.is_available_for_booking ? 'calendar-event-available' : event.meta?.marketing_plan ? 'calendar-event-marketing' : 'calendar-event-meeting',
          barColor: event.is_available_for_booking ? "#28a879" : "#2f85d8"
        })
      }
      return eventsArray;
    },
    async deleteEvent(eventData) {
      if (eventData.checkEventChange) {
        this.showDialogToUser(eventData.event, 'Potvrdením vymažete túto udalosť');
        const userPermission = await this.waitForUserPermission();
        if (!userPermission) return;
      }
      await this.$store.dispatch("clientsEvents/deleteEvent", eventData.event.e.data.id);
    },
    eventExtendedView(event) {
      if (event.e.data.meta?.marketing_plan && this.$store.getters["userState/getRole"] === "alex_admin") {
        this.$router.push({name: "MarketingStudio", query: {tab: "calendar", plan: event.e.data.meta.marketing_plan, date: event.e.data.start.slice(0,10)}});
        this.$store.commit("appState/setCalendarVisible", false);
        return;
      }
      const myId = this.$store.getters["userState/getId"];
      const clientId = this.getUserIdFromEventUsers(event.e.data);
      const userIndex = this.eventUsers.map(e => e.id).indexOf(clientId);
      const client = this.eventUsers[userIndex];
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: {delete: true},
        payload: {event: {...event.e.data, users: [...event.e.data.users, myId]}, client: client},
        component: "ExtendedViewPopUp",
        rules: ["single"],
      });
    },
    isEventMovePossible(event) {
      const oldEventIndex = this.events.map(e => e.id).indexOf(event.id);
      const oldEvent = this.events[oldEventIndex];
      if (event.start.value === oldEvent.start.value) return false;
      const isFutureDate = event.start.value > this.todayFull;
      if (!isFutureDate) {
        this.$store.commit("appState/setSuccessMessage", {
          text: `Zadajte budúci dátum`,
          type: "error"
        });
        return false;
      }
      return true;
    },
    async moveEvent(eventData) {
      if (!this.isEventMovePossible(eventData.event)) return;
      const messageText = `Naozaj chcete presunúť ${this.translateEventType(eventData.event.type).toLowerCase()} na ${this.transformDate(eventData.event.start.value)}`
      this.showDialogToUser(eventData.event, messageText);
      const userPermission = await this.waitForUserPermission();
      if (!userPermission) return;
      const myId = this.$store.getters["userState/getId"];
      const newEventData = {
        ...eventData.event,
        start: eventData.event.start.value,
        end: eventData.event.end.value,
        users: [...eventData.event.users, myId]
      }
      this.$store.dispatch("clientsEvents/moveEvent", newEventData);
    },
    showDialogToUser (event, messageText) {
      this.$store.commit("appState/setSuccessMessage", {
        text: messageText,
        type: "confirmation"
      });
    },
    waitForUserPermission () {
      return new Promise((resolve) => {
        const int = setInterval(() => {
          const userConfirmation = this.$store.getters["appState/getUserConfirmation"];
          if (userConfirmation === true) {
            this.$store.commit("appState/setUserConfirmation", null);
            clearInterval(int);
            resolve(true);
          } else if (userConfirmation === false) {
            this.$store.commit("appState/setUserConfirmation", null);
            clearInterval(int);
            resolve(false);
          }
        }, 100);
      });
    },
    nextWeek() { this.shiftDate(1); },
    previousWeek() { this.shiftDate(-1); }
  },
  data: () => ({
    eventUsers: [],
    calendarEvents: [],
    deleting: false,
    viewportWidth: window.innerWidth, mode: 'WorkWeek',
    focusDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
    resizeObserver: null,
    calendar: null,
    events: [],
    todayLong: new Date().toISOString().substr(0, 10),
    todayFull: null,
    currentMonday: null
  }),
}
</script>

<style lang="scss">
.calendar-workspace { height:100%; min-height:0; display:flex; flex-direction:column; gap:20px; color:var(--app-text); }
.calendar-heading { display:flex; align-items:center; justify-content:space-between; gap:16px; }
.calendar-heading__title { display:flex; align-items:center; gap:13px; min-width:0; }
.calendar-heading__icon { display:grid; place-items:center; width:46px; height:46px; flex-shrink:0; border:1px solid var(--app-border); border-radius:14px; background:var(--app-primary-soft); color:var(--app-primary); }
.calendar-eyebrow { color:var(--app-primary); font-size:10px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; }
.calendar-heading h2 { margin:3px 0 0; font-size:24px; font-weight:650; line-height:1.25; color:var(--app-text); }
.calendar-workspace button { font:inherit; cursor:pointer; }
.calendar-icon-button { display:grid; place-items:center; width:34px; height:34px; flex:0 0 34px; padding:0; border:0; border-radius:9px; color:var(--app-text-muted); background:transparent; }
.calendar-icon-button:hover { background:var(--app-primary-soft); color:var(--app-primary); }
.calendar-close { border:1px solid var(--app-border); background:var(--app-surface); }
.calendar-arrow-back { transform:rotate(180deg); }
.calendar-toolbar,.calendar-date-controls,.calendar-toolbar__actions { display:flex; align-items:center; gap:14px; }
.calendar-toolbar { justify-content:space-between; flex-wrap:wrap; gap:12px; }
.calendar-date-controls { gap:10px; }
.calendar-today-button { padding:8px 14px; border:1px solid var(--app-border); border-radius:9px; background:var(--app-surface); color:var(--app-text); font-size:12px!important; font-weight:600!important; }
.calendar-today-button:hover { border-color:var(--app-primary); }
.calendar-arrows { display:flex; gap:1px; }
.calendar-range { font-size:15px; font-weight:600; line-height:1.4; }
.calendar-view-switch { display:flex; padding:3px; background:var(--app-surface-muted); border:1px solid var(--app-border); border-radius:10px; }
.calendar-view-switch button { padding:6px 12px; border:0; background:transparent; border-radius:7px; color:var(--app-text-muted); font-size:11px; }
.calendar-view-switch button[aria-pressed="true"] { background:var(--app-surface); color:var(--app-primary); box-shadow:0 1px 5px rgba(10,25,83,.08); }
.calendar-create-button { display:flex; align-items:center; justify-content:center; gap:6px; padding:10px 14px; border:0; border-radius:10px; background:var(--app-primary); color:var(--app-on-primary,#fff); font-size:12px!important; font-weight:600!important; white-space:nowrap; }
.calendar-create-button:hover { filter:brightness(1.08); }
.calendar-workspace button:focus-visible { outline:2px solid var(--app-primary); outline-offset:3px; }
.calendar-grid-card { flex:1; min-height:160px; display:flex; flex-direction:column; overflow:hidden; border:1px solid var(--app-border); border-radius:16px; background:var(--app-surface); }
.calendar-day-headers { display:grid; flex:0 0 76px; border-bottom:1px solid var(--app-border); padding-right:10px; }
.calendar-timezone { display:flex; align-items:flex-end; justify-content:center; padding-bottom:12px; color:var(--app-text-muted); font-size:10px; }
.calendar-day { display:flex; align-items:center; justify-content:center; gap:9px; border-left:1px solid var(--app-border); }
.calendar-day>span { color:var(--app-text-muted); font-size:11px; text-transform:capitalize; }
.calendar-day>strong { display:grid; place-items:center; width:34px; height:34px; border-radius:11px; font-size:21px; font-weight:600; }
.calendar-day.is-today { background:var(--app-primary-soft); }
.calendar-day.is-today>strong { background:var(--app-primary); color:var(--app-on-primary,#fff); }
.calendar-day>small { color:var(--app-primary); font-size:10px; }
.calendar-canvas { flex:1; min-height:0; overflow:hidden; position:relative; }
.calendar-canvas>div { flex-shrink:0; }
.calendar-footer { display:flex; align-items:center; justify-content:space-between; gap:10px; color:var(--app-text-muted); font-size:10px; }
.calendar-selection-hint,.calendar-legend,.calendar-legend>span { display:flex; align-items:center; gap:6px; }
.calendar-legend { gap:16px; }.calendar-legend i { width:7px; height:7px; border-radius:3px; }
.calendar-legend__meeting { background:var(--app-primary); }.calendar-legend__available { background:#28a879; }
.broker-shell .calendar-workspace .calendar_default_main { font-family:inherit!important; border:0!important; }
.broker-shell .calendar-workspace .calendar_default_main * { border-color:var(--app-border)!important; }
.broker-shell .calendar-workspace .calendar_default_scrollable { scrollbar-width:thin; scrollbar-color:var(--app-border) transparent; }
.broker-shell .calendar-workspace .calendar_default_cell_inner { background:var(--app-surface-muted)!important; border-bottom-style:dotted!important; }
.broker-shell .calendar-workspace .calendar_default_cell_business .calendar_default_cell_inner { background:var(--app-surface)!important; }
.broker-shell .calendar-workspace .calendar_default_rowheader_inner { background:var(--app-surface)!important; color:var(--app-text-muted)!important; font-family:inherit!important; font-size:11px!important; font-weight:500!important; text-align:center; padding-top:7px; }
.broker-shell .calendar-workspace .calendar_default_rowheader_inner>div { font-size:11px!important; padding:0!important; }
.broker-shell .calendar-workspace .calendar_default_rowheader_minutes { font-size:9px!important; opacity:.65; padding-left:2px; }
.broker-shell .calendar-workspace .calendar_default_event_inner { border:1px solid var(--app-border)!important; border-left:3px solid var(--app-primary)!important; border-radius:9px!important; padding:2px 6px!important; background:var(--app-primary-soft)!important; color:var(--app-text)!important; font-size:10px; line-height:1.2; overflow:hidden; }
.broker-shell .calendar-workspace .calendar-event-available .calendar_default_event_inner { border-left-color:#28a879!important; background:color-mix(in srgb, #28a879 10%, var(--app-surface))!important; }
.broker-shell .calendar-workspace .calendar-event-marketing .calendar_default_event_inner { border-left-color:#9575cd!important; }
.calendar-event-compact { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-size:10px; }
.calendar-event-time { font-size:10px; font-weight:650; margin-bottom:1px; }.calendar-event-title { overflow:hidden; text-overflow:ellipsis; }
.broker-shell .calendar-workspace .calendar_default_shadow_inner { background:var(--app-primary-soft)!important; border:1px solid var(--app-primary)!important; border-radius:8px; }
@media(max-width:767px) { .calendar-workspace { gap:15px; }.calendar-heading h2 { font-size:21px; }.calendar-heading__icon { width:40px;height:40px; }.calendar-toolbar__actions { gap:8px; }.calendar-range { font-size:13px; }.calendar-selection-hint { display:none; }.calendar-footer { justify-content:flex-end; } }
@media(max-width:600px) { .calendar-toolbar { align-items:stretch; }.calendar-date-controls { flex:1 0 100%; }.calendar-toolbar__actions { justify-content:flex-end; width:100%; }.calendar-create-button { padding:9px 12px; }.calendar-day-headers { flex-basis:64px; }.calendar-heading h2 { font-size:20px; }.calendar-grid-card { border-radius:12px; } }
.signature-d-flex {
  display: flex;

  @media(max-width: 600px) {
    flex-direction: column;
  }
}

.plan-signature {
  display: block !important;
}

.controls {
  display: flex;
  justify-content: space-between;
  margin: 1rem 0;
}

.places {
  margin: 1rem 0;
  font-size: 1.2rem;

  & .place-item {
    margin: 1rem 0;
    padding: 2rem;
    box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.05);
    border-radius: 10px;


    @media (max-width: 479px) {
      &, & .fold {
        flex-direction: column;
      }

      & .fold .align-self-center {
        align-self: start;
      }
    }
  }
}

.address-select {
  margin-right: 10px;
}

#autocomplete-input {
  margin: 0 1rem;
  border: none !important;
  outline: none !important;
  width: 100%;

  @media (max-width: 479px) {
    margin: 0;
  }
}

.calendar-control {
  display: flex;
  justify-content: space-between;
  margin: 1rem 0;

  & img {
    transition: all ease-in-out .3s;
    width: 30px;
    height: 30px;

    &:hover {
      &.mirrored {
        transform: scaleX(-1.2) scaleY(1.2) !important;
      }

      transform: scale(1.2);
      cursor: pointer;
    }
  }
}

.mirrored {
  -webkit-transform: scaleX(-1);
  transform: scaleX(-1);
}

.calendar-icon {
  max-width: 100%;
  margin-top: 0px;
  margin-bottom: 0px;
}


</style>
